import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Sprout, X } from 'lucide-react';
import nurseryService from '@/services/nursery';
import type { NurseryLocation, NurseryLot, RowSide } from '@/types/nursery';
import type { SprayTargetInput } from '@/types/spray';
import {
  buildNurseryLayout, parseRowList, summariseTargets, targetKey,
  type NurseryTargetSummary, type NurserySprayLayout,
} from '@/utils/nurserySprayTargets';
import { cn } from '@/utils/cn';

const PLACE_TYPE: Record<string, string> = {
  seed_bed: 'Seed bed', polyhouse: 'Polyhouse', container_area: 'Container area',
  field: 'Field', other: 'Place', nursery_row: 'Row position',
  shop_floor: 'Shop floor', storeroom: 'Storeroom',
};

interface Props {
  farmId: number;
  value: SprayTargetInput[];
  onChange: (targets: SprayTargetInput[]) => void;
  /** Plants and lots in what is ticked, counted once each. */
  onSummary?: (summary: NurseryTargetSummary) => void;
  error?: string;
}

/**
 * Pick what a nursery spray is aimed at: whole blocks, rows (or one side of
 * a row), single positions on a row, and places that are not rows. Every line
 * says how many plants stand there, so somebody spraying a polyhouse can see
 * it is the one with the trees in it.
 */
function NurseryTargetSelector({ farmId, value, onChange, onSummary, error }: Props) {
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [lots, setLots] = useState<NurseryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [blockId, setBlockId] = useState<number | null>(null);
  const [rangeText, setRangeText] = useState('');
  const [rangeSide, setRangeSide] = useState<'both' | RowSide>('both');
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      nurseryService.getLocations({ farm_id: farmId, active: true }),
      nurseryService.getLots({ farm_id: farmId, status: 'active' }),
    ]).then(([locRes, lotRes]) => {
      if (cancelled) return;
      setLocations(locRes.success && Array.isArray(locRes.data) ? locRes.data : []);
      setLots(lotRes.success && Array.isArray(lotRes.data) ? lotRes.data : []);
    }).catch((e: unknown) => {
      if (!cancelled) setLoadError(e instanceof Error ? e.message : 'The nursery places could not be read.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [farmId]);

  const layout: NurserySprayLayout = useMemo(() => buildNurseryLayout(locations, lots), [locations, lots]);

  // Open on the block the ticked targets are in, else the first.
  useEffect(() => {
    if (blockId != null && layout.blocks.some(b => b.id === blockId)) return;
    const ticked = value.find(t => t.target_type !== 'nursery_location');
    setBlockId(ticked?.target_id ?? layout.blocks[0]?.id ?? null);
  }, [layout, blockId, value]);

  const summary = useMemo(() => summariseTargets(value, layout), [value, layout]);
  useEffect(() => { onSummary?.(summary); }, [summary, onSummary]);

  const keys = useMemo(() => new Set(value.map(targetKey)), [value]);
  const block = layout.blocks.find(b => b.id === blockId) ?? null;
  const wholeBlockTicked = block != null && keys.has(`nursery_block:${block.id}`);

  const add = (targets: SprayTargetInput[]) => {
    const next = [...value];
    const have = new Set(keys);
    for (const t of targets) {
      const k = targetKey(t);
      if (!have.has(k)) { next.push(t); have.add(k); }
    }
    onChange(next);
  };
  const removeKey = (k: string) => onChange(value.filter(t => targetKey(t) !== k));
  const toggle = (t: SprayTargetInput) => {
    const k = targetKey(t);
    if (keys.has(k)) removeKey(k); else add([t]);
  };

  const rowTarget = (row: number, side: RowSide | null): SprayTargetInput =>
    ({ target_type: 'nursery_row', target_id: block!.id, row_number: row, row_side: side });

  // A whole block takes the place of its rows and positions: they are in it.
  const toggleWholeBlock = () => {
    if (!block) return;
    const k = `nursery_block:${block.id}`;
    if (keys.has(k)) { removeKey(k); return; }
    const inBlock = new Set(block.rows.flatMap(r => r.slots.map(s => s.id)));
    onChange([
      ...value.filter(t =>
        !(t.target_type === 'nursery_row' && t.target_id === block.id)
        && !(t.target_type === 'nursery_location' && inBlock.has(t.target_id))),
      { target_type: 'nursery_block', target_id: block.id },
    ]);
  };

  const addRange = () => {
    if (!block) return;
    const wanted = new Set(parseRowList(rangeText));
    const lines = block.rows.filter(r => wanted.has(r.row)
      && (rangeSide === 'both' || r.side === rangeSide || r.side == null));
    add(lines.map(r => rowTarget(r.row, r.side)));
    setRangeText('');
  };

  const label = (t: SprayTargetInput): string => {
    if (t.target_type === 'nursery_block') {
      return `${layout.blocks.find(b => b.id === t.target_id)?.name ?? 'Block'} (whole block)`;
    }
    if (t.target_type === 'nursery_row') {
      const name = layout.blocks.find(b => b.id === t.target_id)?.name ?? 'Block';
      return `${name}, row ${t.row_number}${t.row_side ? ` ${t.row_side}` : ''}`;
    }
    const loc = layout.locationById.get(t.target_id);
    if (!loc) return 'Place';
    return loc.location_type === 'nursery_row' && loc.block_name ? `${loc.block_name} ${loc.name}` : loc.name;
  };

  if (loading) return <p className="text-sm text-neutral-400">Loading the nursery…</p>;
  if (loadError) {
    return (
      <p className="text-sm text-red-600 break-words">
        {loadError} Spraying nursery stock needs a nursery seat as well as a spray seat.
      </p>
    );
  }
  if (!layout.blocks.length && !layout.places.length) {
    return (
      <p className="text-sm text-neutral-500">
        This farm has no nursery rows or places yet. Add them on the Locations page first.
      </p>
    );
  }

  const plants = (n: number) => `${n.toLocaleString()} plant${n === 1 ? '' : 's'}`;

  return (
    <div className="space-y-4">
      {value.length > 0 && (
        <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200 space-y-2">
          <div className="flex flex-wrap gap-2">
            {value.map(t => (
              <span key={targetKey(t)}
                className="inline-flex max-w-full items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-3 py-1 text-sm">
                <Sprout className="h-3.5 w-3.5 shrink-0 text-green-600" />
                <span className="min-w-0 break-words">{label(t)}</span>
                <button type="button" aria-label={`Remove ${label(t)}`} onClick={() => removeKey(targetKey(t))}
                  className="shrink-0 text-neutral-400 hover:text-red-500">
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-neutral-600">
            <strong>{plants(summary.plants)}</strong> in {summary.lots} lot{summary.lots === 1 ? '' : 's'}
            {summary.empty > 0 && (
              <span className="text-amber-700"> · {summary.empty} of these {summary.empty === 1 ? 'has' : 'have'} nothing standing in {summary.empty === 1 ? 'it' : 'them'}</span>
            )}
          </p>
        </div>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}

      {layout.blocks.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-neutral-700">Rows</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              aria-label="Nursery block"
              value={blockId ?? ''}
              onChange={e => setBlockId(Number(e.target.value))}
              className="w-full sm:w-auto border border-neutral-300 rounded-lg px-3 py-2 text-sm"
            >
              {layout.blocks.map(b => (
                <option key={b.id} value={b.id}>{b.name} — {plants(b.plants)}</option>
              ))}
            </select>
            {block && (
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={wholeBlockTicked} onChange={toggleWholeBlock} />
                Whole block ({plants(block.plants)})
              </label>
            )}
          </div>

          {block && !wholeBlockTicked && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  aria-label="Rows to add"
                  value={rangeText}
                  onChange={e => setRangeText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRange(); } }}
                  placeholder="Rows, e.g. 1-12, 15"
                  className="w-40 border border-neutral-300 rounded-lg px-3 py-1.5 text-sm"
                />
                <select
                  aria-label="Side of the track"
                  value={rangeSide}
                  onChange={e => setRangeSide(e.target.value as 'both' | RowSide)}
                  className="border border-neutral-300 rounded-lg px-2 py-1.5 text-sm"
                >
                  <option value="both">Both sides</option>
                  <option value="left">Left</option>
                  <option value="right">Right</option>
                </select>
                <button type="button" onClick={addRange} disabled={!parseRowList(rangeText).length}
                  className="px-3 py-1.5 rounded-lg border border-primary-300 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-40">
                  Add rows
                </button>
              </div>

              <ul className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-80 overflow-y-auto">
                {block.rows.map(r => {
                  const rowTicked = keys.has(targetKey(rowTarget(r.row, r.side)))
                    || (r.side != null && keys.has(targetKey(rowTarget(r.row, null))));
                  const open = openRows.has(r.key);
                  return (
                    <li key={r.key} className="px-3 py-2">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <label className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                          <input type="checkbox" checked={rowTicked} onChange={() => toggle(rowTarget(r.row, r.side))} />
                          <span className="font-medium text-neutral-800">{r.label}</span>
                          <span className="text-xs text-neutral-500">
                            {r.slots.length} position{r.slots.length === 1 ? '' : 's'} · {plants(r.plants)}
                          </span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setOpenRows(prev => {
                            const next = new Set(prev);
                            if (next.has(r.key)) next.delete(r.key); else next.add(r.key);
                            return next;
                          })}
                          className="inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
                          aria-expanded={open}
                        >
                          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          Positions
                        </button>
                      </div>
                      {open && (
                        <div className="mt-2 flex flex-wrap gap-1.5 pl-6">
                          {r.slots.map(slot => {
                            const t: SprayTargetInput = { target_type: 'nursery_location', target_id: slot.id };
                            const on = rowTicked || keys.has(targetKey(t));
                            const n = layout.plantsByLocation.get(slot.id) ?? 0;
                            return (
                              <button
                                key={slot.id}
                                type="button"
                                disabled={rowTicked}
                                onClick={() => toggle(t)}
                                aria-pressed={on}
                                className={cn(
                                  'px-2 py-1 rounded-md border text-xs',
                                  on ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-neutral-300 text-neutral-700',
                                  rowTicked && 'opacity-60',
                                )}
                              >
                                {slot.sequence_number ?? slot.name}{n > 0 ? ` · ${n}` : ''}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
                {!block.rows.length && <li className="px-3 py-2 text-sm text-neutral-500">This block has no rows.</li>}
              </ul>
            </>
          )}
          {wholeBlockTicked && (
            <p className="text-xs text-neutral-500">The whole block is ticked, which takes in every row and position in it.</p>
          )}
        </section>
      )}

      {layout.places.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-neutral-700">Places</h3>
          <ul className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
            {layout.places.map(p => {
              const t: SprayTargetInput = { target_type: 'nursery_location', target_id: p.location.id };
              return (
                <li key={p.location.id} className="px-3 py-2">
                  <label className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                    <input type="checkbox" checked={keys.has(targetKey(t))} onChange={() => toggle(t)} />
                    <span className="font-medium text-neutral-800 break-words">{p.location.name}</span>
                    <span className="text-xs text-neutral-500">
                      {PLACE_TYPE[p.location.location_type] ?? 'Place'}
                      {p.location.block_name ? ` in ${p.location.block_name}` : ''} · {plants(p.plants)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}

export default NurseryTargetSelector;
